import { useState } from "react";

import {
  useMyStorefrontOfferings,
  type OfferingState,
} from "../items/application/hooks/useMyStorefrontOfferings";
import { StorefrontBulkPriceDialog } from "./StorefrontBulkPriceDialog";
import { StorefrontPriceInput } from "./StorefrontPriceInput";

interface Props {
  storefrontId: string;
  storefrontName: string;
  /** 이 몰의 표시가 축. false 면 공급가액(부가세 별도)이다 */
  includeVat: boolean;
}

const won = (n: number | null | undefined) =>
  n == null ? "-" : `${n.toLocaleString("ko-KR")}원`;

const STATE_LABEL: Record<OfferingState, string> = {
  all: "전체",
  listed: "노출 중",
  unpriced: "가격 미입력",
  inactive: "노출 중지",
};

const CATEGORY_LABEL: Record<string, string> = {
  EXEMPT: "면세",
  ZERO_RATED: "영세",
};

/**
 * 공급사 «쇼핑몰» 화면.
 *
 * 물품관리 표에 몰 열을 하나 더 붙이는 대신, 몰을 축으로 목록을 뒤집는다.
 * 승인분이 수만 줄 사이에 흩어져 있으면 «어디 있는지» 부터 문제가 되기 때문이다.
 *
 * 상단 퍼널이 이 화면의 요점이다 — 승인 → 가격 입력 → 노출 세 단계 중 어디서
 * 막혀 있는지가 한눈에 보여야 한다.
 */
export function StorefrontOfferingsPanel({
  storefrontId,
  storefrontName,
  includeVat,
}: Props) {
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const {
    items,
    total,
    counts,
    isLoading,
    isFetching,
    state,
    setState,
    search,
    setSearch,
    page,
    setPage,
    pageSize,
    runBulkPrice,
    runBulkActive,
    setItemPrice,
    isBulkPending,
  } = useMyStorefrontOfferings(storefrontId);

  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="flex flex-col gap-3">
      {/* 퍼널 */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3 text-sm">
        <span className="font-semibold text-emerald-800">{storefrontName}</span>
        <span className="text-emerald-900">
          승인 <b className="tabular-nums">{counts.all.toLocaleString("ko-KR")}</b>
          <span className="mx-1 text-emerald-600">→</span>
          노출 <b className="tabular-nums">{counts.listed.toLocaleString("ko-KR")}</b>
        </span>
        {counts.unpriced > 0 ? (
          <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
            가격 미입력 {counts.unpriced.toLocaleString("ko-KR")}건 — 이 상품들은
            아직 안 팔립니다
          </span>
        ) : null}
        <div className="ml-auto flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => setIsBulkOpen(true)}
            disabled={counts.all === 0}
            className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
          >
            가격 일괄 설정
          </button>
          <button
            type="button"
            disabled={isBulkPending || counts.listed === 0}
            onClick={() => {
              if (
                window.confirm(
                  `${storefrontName}에 노출 중인 내 상품을 모두 내릴까요?\n승인은 남으므로 언제든 다시 켤 수 있습니다.`,
                )
              ) {
                runBulkActive(false);
              }
            }}
            className="rounded-lg border border-border px-3 py-1.5 text-xs text-muted-foreground disabled:opacity-40"
          >
            노출 일괄 중지
          </button>
          {counts.inactive > 0 ? (
            <button
              type="button"
              disabled={isBulkPending}
              onClick={() => runBulkActive(true)}
              className="rounded-lg border border-border px-3 py-1.5 text-xs text-muted-foreground disabled:opacity-40"
            >
              중지 {counts.inactive}건 재개
            </button>
          ) : null}
        </div>
      </div>

      {/* 상태 탭 + 검색 */}
      <div className="flex flex-wrap items-center gap-2">
        {(Object.keys(STATE_LABEL) as OfferingState[]).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setState(key)}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
              state === key
                ? "bg-emerald-600 text-white"
                : "border border-border text-muted-foreground"
            }`}
          >
            {STATE_LABEL[key]}
            <span className="ml-1 tabular-nums">
              {key === "all"
                ? counts.all
                : key === "listed"
                  ? counts.listed
                  : key === "unpriced"
                    ? counts.unpriced
                    : counts.inactive}
            </span>
          </button>
        ))}
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="품명 검색"
          className="ml-auto w-56 rounded-lg border border-border px-3 py-1.5 text-sm"
        />
      </div>

      {/* 목록 */}
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/30 text-xs text-muted-foreground">
              <th className="px-3 py-2 text-left font-medium">품목</th>
              <th className="px-3 py-2 text-right font-medium">내 단가</th>
              <th className="px-3 py-2 text-right font-medium">
                몰 판매가
                <span className="ml-1 font-normal text-2xs text-muted-foreground">
                  ({includeVat ? "부가세 포함" : "공급가액"})
                </span>
              </th>
              <th className="px-3 py-2 text-center font-medium">상태</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={4} className="py-12 text-center text-muted-foreground">
                  불러오는 중…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-12 text-center text-muted-foreground">
                  {counts.all === 0
                    ? "이 쇼핑몰에 승인된 상품이 아직 없습니다. 세모 관리자가 승인하면 여기에 나타납니다."
                    : "조건에 맞는 상품이 없습니다"}
                </td>
              </tr>
            ) : (
              items.map((o) => (
                <tr
                  key={o.offeringId}
                  className={`border-b border-border/40 last:border-0 ${
                    o.isActive ? "" : "opacity-50"
                  }`}
                >
                  <td className="px-3 py-2">
                    <span className="block">
                      {o.name}
                      {/*
                        면세·영세는 부가세가 붙지 않는다. 공급가액과 총액이 같으므로
                        여기 표시된 값이 곧 손님이 내는 값이다.
                      */}
                      {o.taxCategory && CATEGORY_LABEL[o.taxCategory] ? (
                        <span className="ml-1.5 rounded bg-primary/5 px-1.5 py-0.5 text-2xs font-medium text-primary-text">
                          {CATEGORY_LABEL[o.taxCategory]}
                        </span>
                      ) : null}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {[o.manufacturer, o.spec1, o.unit]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                    {won(o.basePrice)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {o.priceSource === "linked" ? (
                      <span className="inline-flex items-center gap-1">
                        <span className="font-semibold tabular-nums">
                          {won(o.price)}
                        </span>
                        <span
                          className="rounded bg-emerald-100 px-1.5 py-0.5 text-2xs font-medium text-emerald-700"
                          title="내 단가에 연동돼 있습니다. 내 단가를 고치면 몰 가격도 따라갑니다."
                        >
                          연동
                          {o.priceMarkupBp !== 0
                            ? ` +${(o.priceMarkupBp / 100).toFixed(1)}%`
                            : ""}
                        </span>
                      </span>
                    ) : (
                      <div className="ml-auto w-32">
                        <StorefrontPriceInput
                          value={o.enteredPrice ?? null}
                          onCommit={(next) => setItemPrice(o.itemId, next)}
                        />
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center text-xs">
                    {o.state === "listed" ? (
                      <span className="rounded bg-emerald-50 px-1.5 py-0.5 font-medium text-emerald-700">
                        노출 중
                      </span>
                    ) : o.state === "unpriced" ? (
                      <span className="rounded bg-amber-50 px-1.5 py-0.5 font-medium text-amber-700">
                        가격 미입력
                      </span>
                    ) : (
                      <span className="rounded bg-muted px-1.5 py-0.5 font-medium text-muted-foreground">
                        노출 중지
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pageCount > 1 ? (
        <div className="flex items-center justify-center gap-2 text-xs">
          <button
            type="button"
            disabled={page === 0 || isFetching}
            onClick={() => setPage(Math.max(0, page - 1))}
            className="rounded-lg border border-border px-3 py-1.5 disabled:opacity-40"
          >
            이전
          </button>
          <span className="text-muted-foreground">
            {page + 1} / {pageCount}
          </span>
          <button
            type="button"
            disabled={page + 1 >= pageCount || isFetching}
            onClick={() => setPage(Math.min(pageCount - 1, page + 1))}
            className="rounded-lg border border-border px-3 py-1.5 disabled:opacity-40"
          >
            다음
          </button>
        </div>
      ) : null}

      {isBulkOpen ? (
        <StorefrontBulkPriceDialog
          storefrontName={storefrontName}
          unpricedCount={counts.unpriced}
          onRun={runBulkPrice}
          onClose={() => setIsBulkOpen(false)}
        />
      ) : null}
    </div>
  );
}
